import { useState, useEffect } from "react";
import { supabase, isSupabaseConnected, supabaseFunctionsBaseUrl } from "@/lib/supabase";
import { Wifi, WifiOff, AlertCircle } from "lucide-react";

export function SupabaseConnectionStatus() {
  const [status, setStatus] = useState<"checking" | "connected" | "disconnected" | "error">("checking");
  const [detail, setDetail] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!isSupabaseConnected || !supabase) {
        setStatus("disconnected");
        setDetail("Supabase client not initialized — check env vars");
        return;
      }
      try {
        // Ping the leads table with a lightweight count query
        const { count, error } = await supabase
          .from("leads")
          .select("*", { count: "exact", head: true });
        if (cancelled) return;
        if (error) {
          console.error("[Supabase Ping] leads table error:", error.message, error);
          setStatus("error");
          setDetail(`Table query failed: ${error.message}`);
          return;
        }
        setStatus("connected");
        setDetail(`Connected — leads table accessible (count: ${count ?? "unknown"})`);
      } catch (err) {
        if (cancelled) return;
        console.error("[Supabase Ping] exception:", err);
        setStatus("error");
        setDetail(`Exception: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    check();
    return () => { cancelled = true; };
  }, []);

  const config = {
    checking: { icon: Wifi, color: "bg-amber-50 text-amber-700 border-amber-200", label: "Checking connection..." },
    connected: { icon: Wifi, color: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "System Connected" },
    disconnected: { icon: WifiOff, color: "bg-red-50 text-red-700 border-red-200", label: "System Offline" },
    error: { icon: AlertCircle, color: "bg-red-50 text-red-700 border-red-200", label: "Connection Error" },
  }[status];

  const Icon = config.icon;

  return (
    <div className={`rounded-lg border px-3 py-2 text-xs flex items-center gap-2 ${config.color}`}>
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="font-medium">{config.label}</span>
      {detail && <span className="text-slate-500">— {detail}</span>}
    </div>
  );
}